import React, { useMemo, useCallback } from 'react';
import { EditRedistributionData } from '../editRedistributionLib';
import { Column } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { useCurrency } from '../../../../hooks/useCurrency';
import StatisticsSummary from '../StatisticsSummary';
import { buildTargetStoreStats } from './targetStoreStats';
import PrefilledTableTargetStore from '../../Statistics/modal/TargetStore/PrefilledTableTargetStore';
import { useApplicationData } from '../../../../hooks/useApplicationData';
import { useDeleteTargetStoresMutation } from '../apiCalculationEdit';
import { useAppDispatch } from '../../../../store';
import { closeModal } from '../../../Modals/modalSlice';
import { processErrorAndShowToast } from '../../../../lib/utils/processErrorAndShowToast';
import BackupOrRemoveButton from '../BackupOrRemoveButton';
import { computeRemSize } from '@ydistri/theme';

type EditRedistributionRouteProps = Pick<
  EditRedistributionData,
  'calculationId' | 'targetStores' | 'useDepartments'
>;

const EditRedistributionTargetStore: React.FC<EditRedistributionRouteProps> = ({
  calculationId,
  targetStores,
  useDepartments,
}) => {
  const currency = useCurrency();
  const { departments: hasDepartments } = useApplicationData();
  const dispatch = useAppDispatch();
  const [deleteTargetStores, deleteTargetStoresStatus] = useDeleteTargetStoresMutation();

  const stats = useMemo(
    () => buildTargetStoreStats(targetStores, hasDepartments, currency),
    [targetStores, hasDepartments, currency],
  );

  const handleDelete = useCallback(() => {
    deleteTargetStores({
      calculationId,
      targetStores: (targetStores ?? []).map((r, i) => ({
        targetStoreId: r.targetStore.id,
        departmentId: r.department?.id,
        description: i === 0 ? `Removed ${targetStores?.length} target stores` : undefined,
        entityDetail: JSON.stringify(r),
      })),
    })
      .then(() => {
        dispatch(addToast({ message: `Target store entries removed successfully.` }));
        dispatch(closeModal());
      })
      .catch(processErrorAndShowToast(dispatch, 'Removing target stores failed'));
  }, [deleteTargetStores, dispatch, calculationId, targetStores]);

  return (
    <Column $gap="2rem" $justifyContent="space-evenly" $padding={0}>
      <div style={{ minHeight: computeRemSize(400) }}>
        <PrefilledTableTargetStore
          data={targetStores}
          displayDepartment={hasDepartments && useDepartments}
        />
      </div>
      <StatisticsSummary title="Summary" stats={stats} />
      <BackupOrRemoveButton
        calculationId={calculationId}
        loading={deleteTargetStoresStatus.isLoading}
        onClick={handleDelete}
      />
    </Column>
  );
};

export default EditRedistributionTargetStore;
